// Native-auth infrastructure for the Apple + Google + verified-phone posture.
//
// STEP-1 (this file): the security-critical plumbing the native trio UI (Step 2)
// will consume — OAuth state/CSRF, deep-link callback validation, JWT-shape
// guard, provider boundary, and the native Apple authorize seam. Authored fresh
// against current main (NOT cherry-picked from the 294-behind line). Nothing here
// is wired into a mounted component yet, so on legacy web it is inert and the
// existing Google/Phone web login is unchanged. Gated dark by
// REACT_APP_QLEARN_IOS_SIGNIN_TRIO at the (future) call site.
import { randomBytes } from 'crypto';
import { api } from './apiService';

// Custom URL scheme the native app registers (iOS Info.plist CFBundleURLTypes);
// the backend redirects the OAuth callback here so iOS routes it back in-app.
export const NATIVE_OAUTH_REDIRECT_URI = 'com.medlearnity.qlearn://oauth2redirect';

export const OAUTH_PROVIDERS = Object.freeze({ GOOGLE: 'google', APPLE: 'apple' });

// C3: a structurally-valid JWT (three base64url segments). We refuse to persist
// or decode anything else — an injected non-JWT would otherwise throw in the
// jwt_decode fetch interceptor on every subsequent request.
export const isLikelyJwt = (token) =>
  typeof token === 'string' &&
  /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token);

// C1: unguessable opaque state correlating an OAuth request with its callback.
// CSPRNG only — a predictable state defeats the OAuth CSRF guard, so we never
// derive it from a non-cryptographic PRNG (Math.random). Browsers and the native
// WKWebView runtime use Web Crypto; Node/SSR/test environments (no Web Crypto)
// use Node's cryptographically secure randomBytes. Either path yields 16 opaque,
// per-request random bytes as 32 hex chars.
export const generateOAuthState = () => {
  const toHex = (bytes) =>
    Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const webCrypto =
    (typeof window !== 'undefined' && (window.crypto || window.msCrypto)) ||
    (typeof globalThis !== 'undefined' && globalThis.crypto) ||
    null;
  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    webCrypto.getRandomValues(bytes);
    return toHex(bytes);
  }
  return toHex(Uint8Array.from(randomBytes(16)));
};

// Strictly validates a native OAuth callback deep link before trusting its token:
// exact scheme+host (never a prefix), state matches the request we sent, and the
// token is a well-formed JWT. Returns { ok, token } — ok:false rejects everything
// (forged host, login-CSRF/injected state, or non-JWT).
export const parseNativeOAuthCallback = (openedUrl, expectedState) => {
  try {
    const parsed = new URL(openedUrl);
    const expected = new URL(NATIVE_OAUTH_REDIRECT_URI);
    if (parsed.protocol !== expected.protocol || parsed.host !== expected.host) {
      return { ok: false };
    }
    if (!expectedState || parsed.searchParams.get('state') !== expectedState) {
      return { ok: false };
    }
    const token = parsed.searchParams.get('token');
    if (!isLikelyJwt(token)) {
      return { ok: false };
    }
    return { ok: true, token };
  } catch (err) {
    return { ok: false };
  }
};

// ASAuthorizationError.canceled is the EXACT code 1001. Classification is CODE-FIRST and
// PRESENCE-DEFINITIVE: if the error carries a `code` PROPERTY at all — even '' / null / 0 /
// any other falsey value — only String(code) === '1001' is a cancel and the message can
// NEVER override it (a non-1001 code is always a real error). Only when there is NO `code`
// property do we consult the message, and then solely an explicitly-typed STRING whose WHOLE
// value (after light normalisation) is one of a narrow allowlist of clean user-cancel
// phrases — never a substring, so negation, service/failure/upstream context, numeric/string
// lookalikes, and any suffix/prefix text all fail, and a non-string message (e.g. an object
// with a coercing toString) is refused outright. The pinned plugin rejects with only
// localizedDescription (no code), so an ambiguous failure surfaces honestly as an error
// rather than a silent cancel.
// The exact cancel code, in its two allowed PRIMITIVE representations. Comparison is strict
// (`===`) with no coercion, so object/array/boxed-Number/bigint/toString/valueOf lookalikes
// (e.g. { toString: () => '1001' }, [1001], new Number(1001), 1001n) never match.
const APPLE_CANCEL_CODE_STR = '1001';
const APPLE_CANCEL_CODE_NUM = 1001;

// Whole-string allowlist of clean user-cancel phrases. Applied to a normalised message
// (trimmed, lowercased, a single run of trailing '.'/'!' removed) and anchored ^…$, so ANY
// extra context — leading "no ", trailing " after a network error", ": E1001_SERVER",
// "(error 10010)", "; code 21001" — fails to match.
const CLEAN_USER_CANCEL = new RegExp(
  '^(?:'
  + 'the user cancel(?:l?ed)?(?: the (?:request|sign-in flow|sign-in|authorization attempt|authorization))?'
  + '|authorization cancel(?:l?ed)? by the user'
  + '|the request was cancel(?:l?ed)? by user'
  + '|cancel(?:l?ed)? by the user'
  + ')$',
);

const isCleanUserCancelMessage = (message) => {
  if (typeof message !== 'string') return false; // typed: only a real string may cancel
  const normalized = message.trim().toLowerCase().replace(/[.!]+$/, '').trim();
  return CLEAN_USER_CANCEL.test(normalized);
};

export const isAppleAuthCancel = (err) => {
  if (err === null || err === undefined) return false;
  if (typeof err === 'string') return isCleanUserCancelMessage(err);
  if (typeof err !== 'object') return false; // numbers/booleans/etc. are never a cancel
  if ('code' in err) {
    // Property presence is definitive — the code decides and the message is NOT consulted.
    // STRICT primitive equality only: never coerce with String(err.code), so a non-primitive
    // code whose toString/valueOf yields 1001 cannot silently cancel a real error.
    const code = err.code;
    return code === APPLE_CANCEL_CODE_STR || code === APPLE_CANCEL_CODE_NUM;
  }
  return isCleanUserCancelMessage(err.message); // no code property → typed whole-string only
};

// Builds the native Google OAuth start URL (system browser → backend redirect to
// the custom scheme). Referral is preserved when present.
export const buildNativeGoogleAuthUrl = ({ domain, state, referralCode }) => {
  const params = new URLSearchParams({ redirect_uri: NATIVE_OAUTH_REDIRECT_URI, state });
  if (referralCode) params.set('ref', referralCode);
  return `${domain}/qlearn/oauth2/authorization/google?${params.toString()}`;
};

// ---- Native Google OAuth: service-owned, serialized, fail-closed attempt lifecycle ----
//
// Capacitor delivers the appUrlOpen event to EVERY armed listener and there is a single
// system-browser sheet, so the service must guarantee at most one live attempt. Two hazards
// are closed atomically through a single admission section (a promise-chain mutex): the whole
// retire → arm → publish → open sequence AND every deep-link callback commit run inside it,
// so nothing interleaves.
//
// FAIL-CLOSED, BOUNDED + JOINED TEARDOWN — the R5 invariant. Two independent timescales:
//   * The CALLER promise (`result`) and every admission call settle within a DEFINED BOUND
//     (`teardownBoundMs`): a never-settling `addListener`, `Browser.open`, `remove`, or
//     `Browser.close` can never hang a caller.
//   * Native operations are JOINED BEYOND that bound — never abandoned. A successor is admitted
//     ONLY once the prior attempt's teardown is ESTABLISHED (its `remove()` fulfilled AND its
//     `Browser.close()` fulfilled); a timed-out/never-settling/throwing/handle-less teardown
//     leaves the owner poisoned, and the RETAINED teardown promise clears ownership if it ever
//     completes later (recovering admission). Successor-safety does not rely on `remove()`
//     fulfilment as proof of native listener removal (per @capacitor/core@8.4.1, whose handle
//     invokes `removeListener` without awaiting it): a FENCE (`superseded`/`settled`) makes a
//     stale callback inert, and the joined `Browser.close` prevents a late close from hitting a
//     successor's sheet. A fulfilled `addListener` without a usable handle is treated as armed
//     but unremovable (poison), never as "nothing armed".
//
// R6 extended the join to the ORIGINAL addListener/Browser.open outcomes. R7 OWNS the Browser
// sheet lifecycle too: it subscribes to `browserFinished` (the user-Done dismissal that, under
// @capacitor/browser@8.0.3, nulls the native controller so a later close() rejects "No active
// window"). A proven user-closed sheet settles/fences the attempt and lets teardown establish
// WITHOUT calling close(); both the App and Browser listeners are removed exactly once. After ANY
// attempted open, a callable+fulfilled close() is conservatively required before a successor is
// admitted — including after an open REJECTION (a native side effect can precede a bridge reject).
//
// R6 extends the join to the ORIGINAL `addListener` and `Browser.open` outcomes themselves (not
// just `remove`/`close`): both are retained and reconciled. Teardown never establishes while a
// prior open is unresolved; a late successful open is closed before a successor is admitted, and a
// late open rejection settles the caller + starts teardown. A late `addListener` handle is removed
// exactly once via its ORIGINAL `remove()`, and a late add rejection recovers an actually-empty
// slot instead of latching poison. A missing / non-callable `Browser.close` is POISON whenever a
// sheet may have opened — it can never count as successful teardown.
//
// R7 owns the Browser `browserFinished` (user-Done) lifecycle; R8 makes a delayed Done repair a
// failed-close race (retained signal, never an irreversible false) and phase-binds a pre-open
// event. R9 fixes teardown COMPOSITION: (B1) an unusable App/Browser handle no longer skips sheet
// reconciliation or cleanup of the independently-usable PEER handle — the possible sheet is closed
// once and each usable original `remove()` is invoked once, with fail-closed poison retained only
// for the genuinely unremovable callback; (B2) after joining the original open, `browserProvenClosed`
// is re-checked before any programmatic close, so a Done proved during the open-join wait yields no
// close; (B3) both usable removals are INVOKED before either is awaited, so a hanging App removal
// cannot prevent the Browser removal from being invoked exactly once.
//
// R10 closes the never-settling-Browser-registration case symmetrically: the `browserFinished`
// registration is BOUNDED before opening — on registration timeout no sheet is opened, the attempt
// is fenced/failed, and teardown invokes the usable App removal + sheet reconciliation WITHOUT
// serializing them behind the hanging arm, while retaining the unresolved registration (late usable
// handle removed once / late rejection recovers an empty slot / late unusable handle stays poison).
let activeAttempt = null;
let admissionChain = Promise.resolve();

const NATIVE_TEARDOWN_BOUND_MS = 10000;

// Race a promise against a bound. Never rejects, never hangs: resolves { ok:true } on fulfil,
// { ok:false, error } on reject, { ok:false, timedOut:true } if neither happens within boundMs.
const settleWithin = (promise, boundMs) => new Promise((resolve) => {
  let done = false;
  const finish = (outcome) => { if (!done) { done = true; clearTimeout(timer); resolve(outcome); } };
  const timer = setTimeout(() => finish({ ok: false, timedOut: true }), boundMs);
  Promise.resolve(promise).then(
    () => finish({ ok: true }),
    (error) => finish({ ok: false, error }),
  );
});

// Run fn as the next link in the serialized admission chain. A link's rejection never
// poisons the chain (the following link still runs); fn's own result is returned to its
// caller. This is the mutex that makes attempt admission and callback commits atomic.
const withAdmission = (fn) => {
  const run = admissionChain.then(fn, fn);
  admissionChain = run.then(() => {}, () => {});
  return run;
};

// Test-only: reset serialized-admission state between specs (no production caller — a real
// new sign-in retires the prior attempt). Keeps a hung deferred test from stalling the next.
export const __resetNativeGoogleOAuthForTests = () => {
  activeAttempt = null;
  admissionChain = Promise.resolve();
};

const failedController = (error) => ({
  result: Promise.resolve({ ok: false, error }),
  dispose: async () => {},
  state: null,
  authorizationUrl: null,
});

// Join a prior attempt's teardown, bounded. Starts (idempotently) the prior's fence + native
// remove()/close() join and returns true ONLY if teardown is ESTABLISHED within boundMs; false
// on timeout/failure. On timeout the prior's retained teardown keeps running and will clear
// ownership if it later establishes, so a subsequent attempt can recover admission.
const joinPriorTeardownBounded = (attempt, boundMs) => new Promise((resolve) => {
  let done = false;
  const finish = (est) => { if (!done) { done = true; clearTimeout(timer); resolve(est === true); } };
  const timer = setTimeout(() => finish(false), boundMs);
  attempt.retire().then((est) => finish(est), () => finish(false));
});

const admitNativeGoogleOAuth = async ({
  appModule,
  browserModule,
  domain,
  referralCode,
  generateState = generateOAuthState,
  teardownBoundMs = NATIVE_TEARDOWN_BOUND_MS,
}) => {
  // Admit only after the prior attempt's teardown is ESTABLISHED (bounded). Otherwise fail
  // closed: the prior owner stays poisoned (its retained teardown clears ownership if it ever
  // completes) and we arm nothing / open no sheet — no two live sheets, no stale-close overlap.
  if (activeAttempt) {
    const established = await joinPriorTeardownBounded(activeAttempt, teardownBoundMs);
    if (!established) {
      return failedController(new Error('prior native attempt teardown not established (fail closed)'));
    }
  }

  const state = generateState();
  const authorizationUrl = buildNativeGoogleAuthUrl({ domain, state, referralCode });

  let resolveResult;
  const result = new Promise((resolve) => { resolveResult = resolve; });
  let settled = false;      // caller result resolved — also fences the callback
  let superseded = false;   // fenced by retire/dispose → callback inert
  let teardownStarted = false;
  let teardownJoin = null;  // retained Promise<established>; late completion clears ownership

  // The ORIGINAL native operation promises are RETAINED and joined by teardown (R6/R7): a
  // timed-out arm/open is never abandoned. The service owns BOTH Capacitor listeners: App
  // `appUrlOpen` (deep-link) and Browser `browserFinished` (the user-Done dismissal — R7 D1). Each
  // listener's original handle is removed exactly once; openPromise is the real open outcome;
  // openAttempted records that a sheet MAY have opened; browserProvenClosed records that the sheet
  // is proven gone (a user Done), so teardown need not — and must not — call close() again.
  let armPromise = null;      // App appUrlOpen listener registration
  let armRejected = false;
  let armHandle = null;
  let browserArmPromise = null; // Browser browserFinished listener registration (optional)
  let browserArmRejected = false;
  let browserHandle = null;
  let browserProvenClosed = false;
  let openPromise = null;
  let openAttempted = false;

  const self = {}; // identity so ownership is only cleared for THIS attempt
  const settleOnce = (o) => { if (!settled) { settled = true; resolveResult(o); } };
  const clearOwnerIfCurrent = () => { if (activeAttempt === self) activeAttempt = null; };

  // remove()/close() are each invoked AT MOST ONCE and their original promises RETAINED (never
  // recalled — a single-use Capacitor handle would reject a second call). Each join resolves
  // true iff the native op FULFILLED. Fulfilment is best-effort; a reject/throw never establishes.
  let removePromise = null;
  const removeJoin = (handle) => {
    if (removePromise) return removePromise;
    let call;
    try { call = handle.remove(); } catch (e) { call = Promise.reject(e); }
    removePromise = Promise.resolve(call).then(() => true, () => false);
    return removePromise;
  };
  let browserRemovePromise = null;
  const browserRemoveJoin = (handle) => {
    if (browserRemovePromise) return browserRemovePromise;
    let call;
    try { call = handle.remove(); } catch (e) { call = Promise.reject(e); }
    browserRemovePromise = Promise.resolve(call).then(() => true, () => false);
    return browserRemovePromise;
  };
  let closePromise = null;
  const closeJoin = () => {
    if (closePromise) return closePromise;
    let call;
    try { call = browserModule.close(); } catch (e) { call = Promise.reject(e); }
    closePromise = Promise.resolve(call).then(() => true, () => false);
    return closePromise;
  };

  // D1 + D2 + D3: reconcile the opened sheet. If no open was attempted there is no sheet. If the
  // sheet is PROVEN gone (a user Done via browserFinished) teardown establishes WITHOUT close(),
  // which under the pinned Browser@8.0.3 would reject "No active window" and poison forever. Any
  // OTHER attempted open — whether the original open fulfilled OR rejected (a native side effect
  // can precede a bridge rejection, R7 D2) — conservatively requires a CALLABLE, FULFILLED close()
  // before a successor is admitted; a missing/non-callable close is POISON (D3).
  // R8 B1: a one-shot signal resolved when a genuine user-Done browserFinished proves the sheet
  // closed. It is the RETAINED PATH: after a close reject/miss (the native controller can be nil'd
  // BEFORE the JS event arrives, so close() rejects "No active window"), teardown awaits this
  // instead of caching an irreversible false, so a delayed browserFinished still establishes the
  // SAME teardown exactly once.
  let resolveBrowserFinished;
  const browserFinishedSignal = new Promise((r) => { resolveBrowserFinished = r; });

  // Reconcile the opened sheet (R7 D1/D2/D3 + R8 B1). No open ⇒ no sheet. A PROVEN user-close
  // (phase-bound browserFinished) ⇒ closed with NO further close(). Otherwise a sheet may be up:
  // require a callable, fulfilled close(); if close is missing/non-callable/rejects, do NOT cache a
  // false — WAIT (an unbounded native join) for a later browserFinished proof. The Browser listener
  // stays alive across this wait; the caller-facing wait is bounded elsewhere.
  const reconcileSheet = async () => {
    if (!openAttempted) return true;
    if (browserProvenClosed) return true;                            // proven Done → already closed
    if (browserModule && typeof browserModule.close === 'function') {
      if (openPromise) { await openPromise.then(() => {}, () => {}); } // join the real open outcome
      if (browserProvenClosed) return true;                          // R9 B2: a genuine Done arrived DURING the open join → no programmatic close
      if (await closeJoin()) return true;                            // callable, fulfilled close
    }
    // Close missing / non-callable / rejected → fail-closed for now, but keep the RETAINED PATH:
    // a delayed browserFinished still establishes this same teardown.
    await browserFinishedSignal;
    return browserProvenClosed === true;
  };

  // Begin teardown (idempotent): FENCE synchronously, JOIN both listener registrations, reconcile
  // the sheet (the Browser listener stays ALIVE until reconciliation establishes — a delayed Done
  // can still resolve it), and only THEN remove BOTH listener handles exactly once. Never caches an
  // irreversible false for the close race. On establishment ownership clears (possibly late).
  const startTeardown = () => {
    if (teardownStarted) return teardownJoin;
    teardownStarted = true;
    superseded = true; // fence
    settleOnce({ ok: false, error: new Error('superseded by a newer sign-in attempt') });
    teardownJoin = (async () => {
      // R10: JOIN the App registration and start its cleanup + sheet reconciliation IMMEDIATELY — a
      // never-settling Browser `browserFinished` registration must NOT serialize the independently
      // available App removal or sheet reconciliation behind it. App cleanup does not depend on the
      // Browser arm.
      if (armPromise) { await armPromise.then(() => {}, () => {}); }
      const appUsable = !!armPromise && !armRejected && !!armHandle && typeof armHandle.remove === 'function';
      const appUnusable = !!armPromise && !armRejected && !appUsable; // fulfilled w/o usable handle
      // R9 B1 / R9 B3: invoke the usable App removal now (its ORIGINAL remove(), once) and reconcile
      // the possible sheet exactly once — even when a peer handle is unusable/unresolved.
      const appRemovePending = appUsable ? removeJoin(armHandle) : Promise.resolve(true);
      const reconciled = await reconcileSheet();
      // ACCOUNT FOR the Browser registration LAST — teardown may not establish until every possibly
      // armed registration is settled, but the App cleanup above is already in flight. A late usable
      // handle is removed exactly once; a late rejection is an actually-empty slot; a late unusable
      // handle retains fail-closed poison. A never-settling arm keeps ownership poisoned (successor
      // fails closed) without ever blocking App/sheet cleanup.
      if (browserArmPromise) { await browserArmPromise.then(() => {}, () => {}); }
      const browserUsable = !!browserArmPromise && !browserArmRejected && !!browserHandle && typeof browserHandle.remove === 'function';
      const browserUnusable = !!browserArmPromise && !browserArmRejected && !browserUsable;
      const browserRemovePending = browserUsable ? browserRemoveJoin(browserHandle) : Promise.resolve(true);
      const appRemoveOk = await appRemovePending;
      const browserRemoveOk = await browserRemovePending;
      // Establish ONLY if the sheet reconciled, both usable removals fulfilled, and neither armed
      // callback is unremovable (fail-closed poison for a genuinely unremovable handle).
      return reconciled && appRemoveOk && browserRemoveOk && !appUnusable && !browserUnusable;
    })();
    teardownJoin.then((est) => { if (est) clearOwnerIfCurrent(); }, () => {});
    return teardownJoin;
  };

  self.retire = () => startTeardown();

  // R7 D1 / R8 B1+B2: a Browser browserFinished event. PHASE-BIND (B2): only mark the sheet PROVEN
  // closed when THIS attempt has actually opened one (openAttempted) — a pre-open/unowned event must
  // never pre-close a FUTURE sheet. Always settle/fence the (cancelled) attempt, resolve the retained
  // signal (B1: so a delayed Done can establish teardown after a failed close race), and start
  // teardown so both listeners are removed exactly once and ownership clears.
  const onBrowserFinished = () => {
    if (openAttempted) browserProvenClosed = true; // phase-bound proof — never for a not-yet-opened sheet
    if (!settled) settleOnce({ ok: false, error: new Error('sign-in browser dismissed by user') });
    resolveBrowserFinished();
    startTeardown();
  };

  // Reconcile the ORIGINAL open promise's LATE settlement (D1). A late success is joined+closed by
  // teardown; a late rejection on a still-live attempt settles the caller and starts teardown so
  // the outcome is never silently discarded.
  const reconcileLateOpen = (ok, err) => {
    if (ok) { if (superseded) startTeardown(); return; } // late success → let teardown close it
    if (!settled) { settleOnce({ ok: false, error: err }); startTeardown(); }
  };

  // Public teardown: settle the caller immediately (bounded — never hang an awaiter), start the
  // fenced+joined teardown, and wait bounded for it. Ownership clears only when teardown actually
  // establishes (possibly later, via the retained join).
  const dispose = async () => {
    settleOnce({ ok: false, error: new Error('sign-in attempt disposed') });
    startTeardown();
    await settleWithin(teardownJoin, teardownBoundMs);
  };

  // Deep-link callback. Cheap synchronous rejects first (stay armed on forged/CSRF); the commit
  // runs INSIDE the admission section, settles the caller immediately, then starts+joins teardown.
  const onAppUrlOpen = ({ url: openedUrl }) => {
    if (settled || superseded) return undefined;
    const parsed = parseNativeOAuthCallback(openedUrl, state);
    if (!parsed.ok) return undefined; // forged host/scheme, wrong/absent state, or non-JWT
    return withAdmission(async () => {
      if (settled || superseded) return; // a newer attempt fenced us before this ran
      settleOnce({ ok: true, token: parsed.token, provider: OAUTH_PROVIDERS.GOOGLE });
      startTeardown();
      await settleWithin(teardownJoin, teardownBoundMs);
    });
  };

  // Publish ownership synchronously BEFORE awaiting addListener (defense-in-depth atop the
  // admission serialization) so no window exists where activeAttempt is empty mid-arm.
  activeAttempt = self;

  // Arm: retain the ORIGINAL addListener promise and reconcile its settlement (D2).
  armPromise = (async () => appModule.addListener('appUrlOpen', onAppUrlOpen))();
  armPromise.then((h) => { armHandle = h; }, () => { armRejected = true; });

  const armOutcome = await settleWithin(armPromise, teardownBoundMs);
  if (armOutcome.error) {
    // addListener rejected → nothing armed → an empty slot: release ownership, settle caller.
    settleOnce({ ok: false, error: armOutcome.error });
    clearOwnerIfCurrent();
    return { result, dispose, state, authorizationUrl };
  }
  if (armOutcome.timedOut) {
    // Never-settling addListener: settle the caller (also FENCES any late-arming callback via
    // `settled`) and START teardown so the RETAINED arm reconciles — a late usable handle is
    // removed and a late rejection recovers the empty slot (D2). Ownership stays poisoned until then.
    settleOnce({ ok: false, error: new Error('native listener admission timed out') });
    startTeardown();
    return { result, dispose, state, authorizationUrl };
  }

  // Own the Browser sheet lifecycle (R7 D1 / R8 B2 / R10): subscribe to browserFinished BEFORE
  // opening (so a user Done is observed and a synchronous pre-open event is phase-bound + fences),
  // then BOUND the registration. Returns true to proceed to open, or false to ABORT — a synchronous
  // pre-open event fenced admission, or a never-settling registration timed out (R10). On abort no
  // sheet is opened; teardown (started by the caller) invokes the usable App removal + sheet
  // reconciliation WITHOUT blocking on the retained Browser arm (a late usable handle is removed
  // once, a late rejection recovers an empty slot, a late unusable handle keeps fail-closed poison).
  const armBrowserAndBound = async () => {
    if (!(browserModule && typeof browserModule.addListener === 'function')) return true;
    // Assign browserArmPromise BEFORE calling addListener, so a SYNCHRONOUS browserFinished sees the
    // retained promise and its eventual handle is still joined + removed exactly once (R8 B2).
    let settleArm;
    let rejectArm;
    browserArmPromise = new Promise((res, rej) => { settleArm = res; rejectArm = rej; });
    browserArmPromise.then((h) => { browserHandle = h; }, () => { browserArmRejected = true; });
    try {
      Promise.resolve(browserModule.addListener('browserFinished', onBrowserFinished)).then(settleArm, rejectArm);
    } catch (e) { rejectArm(e); }
    if (settled || superseded) return false;                       // a synchronous pre-open event fenced us
    // R10: bound the registration. Fulfil/reject are safely-known outcomes → proceed (a rejection
    // just means no dismissal listener is armed); a TIMEOUT is NOT known → abort (no sheet opens).
    const outcome = await settleWithin(browserArmPromise, teardownBoundMs);
    if (outcome.timedOut) {
      settleOnce({ ok: false, error: new Error('native browser listener registration timed out') });
      return false;
    }
    return !(settled || superseded);                               // a Done during the wait also fences
  };
  if (!(await armBrowserAndBound())) {
    startTeardown();
    return { result, dispose, state, authorizationUrl };
  }

  // Open: retain the ORIGINAL open promise (never only handed to settleWithin) and reconcile its
  // late settlement (D1). Bound the admission so the caller does not hang.
  openAttempted = true;
  openPromise = (async () => browserModule.open({ url: authorizationUrl }))();
  openPromise.then(() => reconcileLateOpen(true), (err) => reconcileLateOpen(false, err));

  const openOutcome = await settleWithin(openPromise, teardownBoundMs);
  if (openOutcome.error) {
    settleOnce({ ok: false, error: openOutcome.error });
    startTeardown();
    await settleWithin(teardownJoin, teardownBoundMs);
  }

  return { result, dispose, state, authorizationUrl };
};

// Runs one native Google OAuth deep-link round-trip. Capacitor App/Browser modules are
// injected (production: dynamic import; tests: mocks). Returns a controller:
//   { result: Promise<{ ok, token?, provider?, error? }>, dispose(), state, authorizationUrl }
// The whole admission runs in the serialized section so the service owns a single active
// attempt: a superseded attempt is retired and can never complete, callbacks are state-
// matched only (CSRF), and completion is single-use (replay-safe).
export const runNativeGoogleOAuth = (params) =>
  withAdmission(() => admitNativeGoogleOAuth(params));

// Native Sign in with Apple seam, aligned to the EXACT pinned
// @capacitor-community/apple-sign-in@8.0.0 contract. Its options are
// { clientId, redirectURI, scopes?: string, state?, nonce? } and the iOS Swift reads
// `call.getString("scopes")` then `.contains("name")` / `.contains("email")` — an array
// (the old `requestedScopes: [0,1]`) is silently ignored, so full-name/email would never be
// requested. We therefore send the `scopes` STRING the plugin actually reads. On error the
// plugin rejects with only `error.localizedDescription` (no native code), so we let that
// rejection propagate UNCHANGED for the caller's isAppleAuthCancel to classify honestly.
export const APPLE_SCOPES = 'name email';

export const authorizeApple = async ({
  importer = () => import('@capacitor-community/apple-sign-in'),
  clientId,
  redirectURI,
  state,
  nonce,
} = {}) => {
  const { SignInWithApple } = await importer();
  const { response } = await SignInWithApple.authorize({
    scopes: APPLE_SCOPES,
    ...(clientId ? { clientId } : {}),
    ...(redirectURI ? { redirectURI } : {}),
    ...(state ? { state } : {}),
    ...(nonce ? { nonce } : {}),
  });
  return response;
};

// Exchanges the Apple identity payload for a QLearn session (backend verifies).
export const appleSignIn = (appleAuthData) => api.appleSignIn(appleAuthData);
